import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe'

import { getUnsubscribeInfo, confirmUnsubscribe } from '../services/api'

function Unsubscribe() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const data = await getUnsubscribeInfo(token)
        setInfo(data)
        if (data.alreadyUnsubscribed) {
          setSuccess(true)
        }
      } catch (err) {
        setError(
          err.response?.data?.detail || 'Invalid or expired unsubscribe link.'
        )
      } finally {
        setLoading(false)
      }
    }
    fetchInfo()
  }, [token])

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await confirmUnsubscribe(token)
      setSuccess(true)
    } catch (err) {
      setError(
        err.response?.data?.detail || 'Something went wrong. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant='h5' gutterBottom>
            Unable to process request
          </Typography>
          <Typography color='text.secondary'>{error}</Typography>
        </Paper>
      </Container>
    )
  }

  if (success) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
          />
          <Typography variant='h5' gutterBottom>
            {info?.alreadyUnsubscribed
              ? 'Already unsubscribed'
              : 'Successfully unsubscribed'}
          </Typography>
          <Typography color='text.secondary'>
            {info?.alreadyUnsubscribed
              ? `You have already been unsubscribed from marketing emails from ${info?.businessName || 'this business'}.`
              : `You have been unsubscribed from marketing emails from ${info?.businessName || 'this business'}. You will still receive important transactional emails about your bookings.`}
          </Typography>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth='sm' sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <UnsubscribeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant='h5' gutterBottom>
          Unsubscribe from marketing emails
        </Typography>
        <Typography color='text.secondary' sx={{ mb: 3 }}>
          Hi {info?.customerFirstName || 'there'}, are you sure you want to
          unsubscribe from marketing emails from{' '}
          <strong>{info?.businessName || 'this business'}</strong>?
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          You will still receive important emails about your bookings, such as
          confirmations and cancellations.
        </Typography>
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={handleConfirm}
          disabled={submitting}
          sx={{ minWidth: 200 }}
        >
          {submitting ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            'Confirm unsubscribe'
          )}
        </Button>
      </Paper>
    </Container>
  )
}

export default Unsubscribe
